import { Box } from '@chakra-ui/layout';
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
import Iframe from 'react-iframe';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthStore } from '@revelio/auth';
import {
  CenterLoading,
  IFrameMessageTypes,
  PagePaths,
  sendMessageToIFrame,
  UserTrackingEvents,
  useListenForIFrameMessages,
} from '@revelio/core';
import { useChartTheme } from '@revelio/replots';

import { environment } from '../../environments/environment';
import { useBrand } from '../../shared/branding/brand-provider';
import { useKibanaLogger } from '../../shared/hooks/kibana-logger/kibana-logger';
import { useUnleashFlags } from '../../shared/unleash-flags/use-unleash-flag';
import styles from './iframed-pages.module.css';

const TAXONOMY_CATALOG_IFRAME_ID = 'taxonomy-catalog-iframe';
const FORCE_UPDATE_VERSION = '1.1';

const TaxonomyCatalog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [iframeUrl, setIframeUrl] = useState<string>();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const { dashMetaCsrfToken, ory, user } = useAuthStore();
  const { isNewTheme } = useChartTheme();
  const { kibanaLogger } = useKibanaLogger();
  const featureFlags = useUnleashFlags();
  const brand = useBrand();

  useEffect(() => {
    mixpanel.track(UserTrackingEvents.PAGE_VISIT, {
      page: 'Taxonomy Catalog',
      path: location.pathname,
    });
    mixpanel.time_event(UserTrackingEvents.PAGE_LEAVE);
    return () => {
      mixpanel.track(UserTrackingEvents.PAGE_LEAVE, {
        page: 'Taxonomy Catalog',
        path: location.pathname,
      });
    };
  }, [location.pathname]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    // Extract the path suffix after '/taxonomy-catalog'
    const pathSuffix = location.pathname.replace(
      `/${PagePaths.TAXONOMY_CATALOG}`,
      ''
    );
    const isLocalTaxonomyCatalog = false; // Set to true to use local hosted taxonomy catalog
    const iframeBaseUrl = isLocalTaxonomyCatalog
      ? `http://localhost:4200${pathSuffix}`
      : `https://taxonomy.reveliolabs.com${pathSuffix}`;
    const iframeUrlWithParams = new URL(`${iframeBaseUrl}${location.search}`);
    iframeUrlWithParams.searchParams.set('v', FORCE_UPDATE_VERSION);
    setIframeUrl(iframeUrlWithParams.toString());
  }, []); // Should only run once on mount. Let the iframe update it's own urls

  useListenForIFrameMessages(
    IFrameMessageTypes.TaxonomyCatalogUrlPath,
    ({ data: pathName, type }) => {
      if (pathName && typeof pathName === 'string') {
        navigate(`/${PagePaths.TAXONOMY_CATALOG}${pathName}`);
      } else {
        console.error('useListenForIFrameMessages: invalid path', {
          data: pathName,
          type,
        });
        kibanaLogger?.error('useListenForIFrameMessages: invalid path', {
          username: user?.email ?? undefined,
        });
      }
    }
  );

  // Send kibanna data to the iframe
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const iframe = document.getElementById(
      TAXONOMY_CATALOG_IFRAME_ID
    ) as HTMLIFrameElement;
    sendMessageToIFrame({
      iframe,
      data: {
        dashMetaCsrfToken,
        ory,
        dashMetaRoot: environment.DASH_META_API,
      },
      type: IFrameMessageTypes.TaxonomyCatalogKibanna,
    });
  }, [dashMetaCsrfToken, iframeLoaded, ory]);

  // Send feature flags to the iframe
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const iframe = document.getElementById(
      TAXONOMY_CATALOG_IFRAME_ID
    ) as HTMLIFrameElement;
    sendMessageToIFrame({
      iframe,
      data: {
        flags: featureFlags,
      },
      type: IFrameMessageTypes.TaxonomyCatalogFeatureFlags,
    });
  }, [iframeLoaded, featureFlags]);

  // Send client name to the iframe for brand resolution
  useEffect(() => {
    if (!iframeLoaded) {
      return;
    }

    const iframe = document.getElementById(
      TAXONOMY_CATALOG_IFRAME_ID
    ) as HTMLIFrameElement;
    sendMessageToIFrame({
      iframe,
      data: {
        clientName: brand.clientName,
      },
      type: IFrameMessageTypes.TaxonomyCatalogBrand,
    });
  }, [iframeLoaded, brand.clientName]);

  return (
    <Box height="100vh" className={styles.container}>
      <Iframe
        id={TAXONOMY_CATALOG_IFRAME_ID}
        key={FORCE_UPDATE_VERSION}
        url={iframeUrl ?? ''}
        width="100%"
        height="100%"
        position="relative"
        styles={{ border: 'none' }}
        onLoad={() => setIframeLoaded(true)}
      />
      {isNewTheme && !iframeLoaded && (
        <CenterLoading className={styles.loader} />
      )}
    </Box>
  );
};

export default TaxonomyCatalog;
